// The territory-first home (owner product direction 2026-10-31; territory-ui.md §7): ball.io's
// front door is the territory game. Laid out like the reference's menu (SF-6): no card, a
// top-aligned column straight on a 0.8 black layer over the live practice arena (the camera
// fixed on the map centre; arena/backdrop.ts before that room's first frame):
// - our wordmark in a 100 px box at the top;
// - a rotating tip line (white, 18 px bold) every 3 s (2 s `ease` fade-in, no fade-out; the
//   touch variant of the controls line on coarse pointers);
// - one 52 px row: the nick field, Play (the territory practice arena: free, no sign-in) and a
//   square slot for the skin button (`setSkinButton`, a hook for the skins work);
// - the money and Classic doors as one compact row (owner: three doors stay); the money card
//   opens in a panel under them ("coming soon" while the server sells none);
// - "N players online"; a three-step "how it works" line in plain white text with our own
//   little drawings (wide screens only);
// - the gear (settings) and "?" (help) in the screen's top corners.
// ≤ 500 px wide the field spans the column and Play wraps under it (SF-13).
// All copy is ours (ui/i18n/en.ts `home.*`, `arena.tip.*`). DOM via ui/dom.ts; the view rules are
// pure functions below (tests).

import { NET_TIMING } from '@ballio/shared';
import type { AssetImage } from '../../assets/loader.js';
import type { UiClickSource } from '../../events/bus.js';
import type { StorageLike } from '../../settings/store.js';
import { BRAND_NAME, createWordmark } from '../brand.js';
import { h, show } from '../dom.js';
import { t } from '../i18n/t.js';
import { gearIcon, helpIcon } from '../icons.js';
import { cssEase } from '../../arena/hud-model.js';
import { onlineText } from './home.js';
import { cleanNickInput, NickPersistence } from './nick.js';

/** Tips rotate this often (UI §7). */
export const TIP_PERIOD_MS = 3000;
/** Each new tip fades in over this long (the old one is cut). */
export const TIP_FADE_MS = 2000;

/** The four tips, in order (the controls line has a touch variant). */
export function homeTips(touch: boolean): string[] {
  return [
    t('arena.tip.invite'),
    touch ? t('arena.tip.controlsTouch') : t('arena.tip.controls'),
    t('arena.tip.rule'),
    t('arena.tip.money'),
  ];
}

/** Which tip shows `ms` after the home opened, and its opacity. */
export function tipAt(ms: number, count: number): { index: number; opacity: number } {
  if (count <= 0) return { index: 0, opacity: 0 };
  const m = Math.max(0, ms);
  const index = Math.floor(m / TIP_PERIOD_MS) % count;
  const into = m % TIP_PERIOD_MS;
  return { index, opacity: cssEase(into / TIP_FADE_MS) };
}

export interface TerritoryPlayState {
  readonly alive: boolean;
  readonly waiting: boolean;
  readonly offline: boolean;
}

/** The Play button: "Play" (resumes while alive), busy while waiting, off while offline. */
export function territoryPlayView(s: TerritoryPlayState): { busy: boolean; disabled: boolean } {
  if (s.alive) return { busy: false, disabled: s.offline };
  return { busy: s.waiting && !s.offline, disabled: s.offline || s.waiting };
}

/** The money door: a live door, "coming soon", or hidden until /api/info.money answered. */
export type MoneyDoor = 'live' | 'soon' | 'unknown';

export function moneyDoorLabel(door: MoneyDoor): string {
  return door === 'live' ? t('home.playMoney') : t('home.playMoneySoon');
}

export interface TerritoryHomeDeps {
  onPlay(nick: string): void;
  onMoney(): void;
  onClassic(): void;
  onSettings(): void;
  onHelp(): void;
  playClick?(source: UiClickSource): void;
  readonly storage?: StorageLike | null;
  readonly touch: boolean;
}

export interface TerritoryHome {
  readonly el: HTMLElement;
  readonly visible: boolean;
  show(nowMs: number): void;
  hide(): void;
  focusNick(): void;
  nick(): string;
  setNick(value: string): void;
  setAlive(alive: boolean): void;
  setWaiting(waiting: boolean): void;
  setConnecting(connecting: boolean): void;
  setOffline(offline: boolean): void;
  setOnline(count: number | null): void;
  setWordmark(img: AssetImage | null): void;
  setMoneyDoor(door: MoneyDoor): void;
  /** The money card (stake select, buy-in) shown in the home's panel; null hides the panel. */
  setMoneyPanel(card: HTMLElement | null): void;
  /**
   * The skin button (a square, Play's height) to the right of Play; null empties the slot. A
   * hook for the skins work: the home itself builds no skin UI.
   */
  setSkinButton(button: HTMLElement | null): void;
  /** A one-line note under Play (e.g. the arena is warming up); null hides it. */
  setNote(text: string | null): void;
  /** Rotates the tip; call while the menu is open (cheap). */
  tick(nowMs: number): void;
  flushNick(): void;
}

const WAIT_TIMEOUT_MS = NET_TIMING.playTimeoutSec * 1000;

/** Our three "how it works" drawings (inline SVG, our own art). */
function stepArt(i: number): string {
  const land = '<rect x="6" y="20" width="18" height="18" rx="5" fill="#3DBE5A"/>';
  const head = (x: number, y: number): string =>
    `<rect x="${x - 4}" y="${y - 4}" width="8" height="8" rx="2" fill="#2E8F45" stroke="#1F6B33" stroke-width="1.5"/>`;
  if (i === 0) {
    return `<svg viewBox="0 0 64 48" aria-hidden="true">${land}<path d="M24 26 C34 26 44 14 52 18 C58 22 54 34 44 36" fill="none" stroke="#3DBE5A" stroke-opacity=".6" stroke-width="5" stroke-linecap="round"/>${head(44, 36)}</svg>`;
  }
  if (i === 1) {
    return `<svg viewBox="0 0 64 48" aria-hidden="true"><path d="M6 20 h18 c10 0 26-8 30 0 c4 8-2 18-18 18 H6z" fill="#3DBE5A"/><path d="M6 40 h30" stroke="#2E8F45" stroke-width="3"/>${head(26, 30)}</svg>`;
  }
  return `<svg viewBox="0 0 64 48" aria-hidden="true"><path d="M6 30 C20 30 30 12 58 14" fill="none" stroke="#E8453C" stroke-opacity=".6" stroke-width="5" stroke-linecap="round"/><path d="M32 6 L32 42" fill="none" stroke="#3D6FE0" stroke-opacity=".6" stroke-width="5" stroke-linecap="round"/><path d="M26 18 l12 12 M38 18 l-12 12" stroke="#33302E" stroke-width="3" stroke-linecap="round"/></svg>`;
}

export function createTerritoryHome(deps: TerritoryHomeDeps): TerritoryHome {
  const persistence = new NickPersistence(deps.storage);
  const state = { alive: false, waiting: false, offline: false, connecting: false };
  let visible = true;
  let openedAtMs = 0;
  let waitTimer: ReturnType<typeof setTimeout> | null = null;
  let door: MoneyDoor = 'unknown';
  const tips = homeTips(deps.touch);

  const wordmark = createWordmark();
  wordmark.el.classList.add('t-wordmark');

  const nickInput = h('input', {
    id: 't-nick',
    class: 't-nick',
    type: 'text',
    name: 'nick',
    placeholder: t('menu.nick.placeholder'),
    autocomplete: 'off',
    autocapitalize: 'off',
    spellcheck: false,
    enterKeyHint: 'go',
    maxLength: 32,
    value: persistence.load(),
  });
  const playSpinner = h('span', { class: 'spinner', 'aria-hidden': 'true', hidden: true });
  const playBtn = h(
    'button',
    { type: 'submit', class: 't-play' },
    playSpinner,
    h('span', { class: 't-play-label' }, t('home.play')),
  );
  const form = h(
    'form',
    {
      class: 't-form',
      novalidate: true,
      onSubmit: (ev: Event) => {
        ev.preventDefault();
        const v = territoryPlayView(state);
        if (v.disabled) return;
        persistence.flush();
        deps.onPlay(nickInput.value);
      },
    },
    h('label', { class: 'sr-only', htmlFor: 't-nick' }, t('menu.nick.label')),
    nickInput,
    playBtn,
  );
  const skinSlot = h('div', { class: 't-skin-slot', hidden: true });
  form.append(skinSlot);
  const note = h('p', { class: 't-note', hidden: true });

  const moneyLabel = h('span', { class: 't-door-label' }, t('home.playMoneySoon'));
  const moneyBtn = h(
    'button',
    {
      type: 'button',
      class: 't-door t-door-money',
      onClick: () => {
        deps.playClick?.('mode');
        deps.onMoney();
      },
    },
    h('span', { class: 't-door-coin', 'aria-hidden': 'true' }, '$'),
    h('span', { class: 't-door-text' }, moneyLabel, h('small', null, t('home.playMoneySub'))),
  );
  const classicBtn = h(
    'button',
    {
      type: 'button',
      class: 't-door t-door-classic',
      onClick: () => {
        deps.playClick?.('mode');
        deps.onClassic();
      },
    },
    h('span', { class: 't-door-ball', 'aria-hidden': 'true' }),
    h('span', { class: 't-door-text' }, t('home.classic'), h('small', null, t('home.classicSub'))),
  );
  const moneyPanel = h('div', { class: 't-money-panel', hidden: true });
  const tipEl = h('p', { class: 't-tip', 'aria-live': 'off' }, tips[0] ?? '');
  const online = h('p', { class: 't-online', 'aria-live': 'off' });
  online.style.visibility = 'hidden';

  const iconBtn = (cls: string, label: string, icon: Node, fn: () => void, src: UiClickSource) =>
    h(
      'button',
      {
        type: 'button',
        class: `t-icon ${cls}`,
        'aria-label': label,
        title: label,
        onClick: () => {
          deps.playClick?.(src);
          fn();
        },
      },
      icon,
    );

  const column = h(
    'section',
    { class: 't-col', 'aria-label': BRAND_NAME },
    h('div', { class: 't-logo' }, wordmark.el),
    h('p', { class: 'sr-only' }, t('home.tagline')),
    tipEl,
    form,
    h('p', { class: 'sr-only' }, t('home.playSub')),
    note,
    h('div', { class: 't-doors' }, moneyBtn, classicBtn),
    moneyPanel,
    online,
  );
  const steps = h(
    'ol',
    { class: 't-steps', 'aria-label': t('home.howto.title') },
    [t('home.howto.1'), t('home.howto.2'), t('home.howto.3')].map((line, i) => {
      const art = h('span', { class: 't-step-art' });
      art.innerHTML = stepArt(i);
      return h('li', { class: 't-step' }, art, h('span', { class: 't-step-text' }, line));
    }),
  );
  const el = h(
    'div',
    { class: 't-home' },
    iconBtn('t-gear', t('settings.title'), gearIcon(), deps.onSettings, 'settings'),
    iconBtn('t-help', t('menu.help'), helpIcon(), deps.onHelp, 'help'),
    column,
    steps,
  );

  nickInput.addEventListener('input', () => {
    const raw = nickInput.value;
    const cleaned = cleanNickInput(raw);
    if (cleaned !== raw) nickInput.value = cleaned;
    persistence.change(nickInput.value);
  });

  function render(): void {
    const v = territoryPlayView(state);
    playBtn.setAttribute('aria-disabled', v.disabled ? 'true' : 'false');
    playBtn.setAttribute('aria-busy', v.busy ? 'true' : 'false');
    playBtn.classList.toggle('is-busy', v.busy);
    playSpinner.hidden = !v.busy;
    moneyBtn.hidden = door === 'unknown';
    moneyLabel.textContent = moneyDoorLabel(door);
    moneyBtn.classList.toggle('is-soon', door === 'soon');
  }

  function focusNick(): void {
    if (!visible || deps.touch) return;
    nickInput.focus({ preventScroll: true });
  }

  render();
  return {
    el,
    get visible() {
      return visible;
    },
    show(nowMs: number): void {
      if (!visible) openedAtMs = nowMs;
      visible = true;
      show(el, true);
      focusNick();
    },
    hide(): void {
      visible = false;
      show(el, false);
    },
    focusNick,
    nick: () => nickInput.value,
    setNick(value: string): void {
      if (nickInput.value !== value) nickInput.value = cleanNickInput(value);
    },
    setAlive(alive: boolean): void {
      state.alive = alive;
      if (alive) state.waiting = false;
      render();
    },
    setWaiting(waiting: boolean): void {
      state.waiting = waiting;
      if (waitTimer !== null) clearTimeout(waitTimer);
      waitTimer = null;
      if (waiting) {
        waitTimer = setTimeout(() => {
          waitTimer = null;
          state.waiting = false;
          render();
        }, WAIT_TIMEOUT_MS);
      }
      render();
    },
    setConnecting(connecting: boolean): void {
      state.connecting = connecting;
      render();
    },
    setOffline(offline: boolean): void {
      state.offline = offline;
      render();
    },
    setOnline(count: number | null): void {
      const text = onlineText(count);
      if (online.textContent !== text) online.textContent = text;
      online.style.visibility = text === '' ? 'hidden' : 'visible';
    },
    setWordmark(img: AssetImage | null): void {
      wordmark.setImage(img);
    },
    setMoneyDoor(d: MoneyDoor): void {
      door = d;
      render();
    },
    setMoneyPanel(cardEl: HTMLElement | null): void {
      if (cardEl === null) {
        moneyPanel.hidden = true;
        return;
      }
      if (cardEl.parentElement !== moneyPanel) moneyPanel.replaceChildren(cardEl);
      moneyPanel.hidden = false;
    },
    setSkinButton(button: HTMLElement | null): void {
      if (button === null) {
        skinSlot.replaceChildren();
        skinSlot.hidden = true;
        return;
      }
      if (button.parentElement !== skinSlot) skinSlot.replaceChildren(button);
      skinSlot.hidden = false;
    },
    setNote(text: string | null): void {
      note.hidden = text === null;
      note.textContent = text ?? '';
    },
    tick(nowMs: number): void {
      if (!visible) return;
      const tip = tipAt(nowMs - openedAtMs, tips.length);
      const text = tips[tip.index] ?? '';
      if (tipEl.textContent !== text) tipEl.textContent = text;
      tipEl.style.opacity = tip.opacity.toFixed(3);
    },
    flushNick(): void {
      persistence.flush();
    },
  };
}
